import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from '../i18n';
import { api } from '../lib/api';
import { apiErrorMessage } from '../lib/errors';
import { formatDateTime } from '../lib/utils';
import { PAYOUT_KEY, PHASE_KEY, phaseTone, RAIL_KEY, railTone } from '../lib/contest';
import type { MyEntrySummary, Payout } from '../types';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import { Alert } from '../components/ui/alert';
import { Button } from '../components/ui/button';
import { FullPageLoader } from '../components/ui/spinner';

/** The signed-in participant's own history: entries, results and payouts. */
export default function MyEntriesPage() {
  const { t, locale } = useTranslation();
  const [entries, setEntries] = useState<MyEntrySummary[]>([]);
  const [payouts, setPayouts] = useState<Payout[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setError(null);
    try {
      const [entryData, payoutData] = await Promise.all([
        api.get<{ entries: MyEntrySummary[] }>('/me/entries'),
        api.get<{ payouts: Payout[] }>('/me/payouts'),
      ]);
      setEntries(entryData.entries);
      setPayouts(payoutData.payouts);
    } catch (error_) {
      setError(apiErrorMessage(error_, t));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    void load();
  }, [load]);

  if (loading) {
    return <FullPageLoader label={t('common.loading')} />;
  }

  return (
    <div className="mx-auto max-w-4xl px-4 py-10">
      <header>
        <h1 className="text-2xl font-semibold tracking-tight text-fg">{t('entries.title')}</h1>
        <p className="mt-1 text-sm text-muted">{t('entries.subtitle')}</p>
      </header>

      {error && (
        <div className="mt-6">
          <Alert tone="error">{error}</Alert>
        </div>
      )}

      {entries.length === 0 && !error && (
        <Card className="mt-8">
          <CardContent className="py-12 text-center text-sm text-muted">
            {t('entries.empty')}
          </CardContent>
        </Card>
      )}

      <ul className="mt-8 flex flex-col gap-4">
        {entries.map((entry) => (
          <li key={entry.contestId}>
            <Card>
              <CardContent className="flex flex-col gap-3 pt-5">
                <div className="flex flex-wrap items-center gap-2">
                  <Badge tone={railTone(entry.rail)}>{t(RAIL_KEY[entry.rail])}</Badge>
                  <Badge tone={phaseTone(entry.phase)}>{t(PHASE_KEY[entry.phase])}</Badge>
                </div>

                <h2 className="text-base font-semibold text-fg">
                  <Link to={`/contests/${entry.contestId}`} className="hover:underline">
                    {entry.title}
                  </Link>
                </h2>

                <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm sm:grid-cols-4">
                  <div>
                    <dt className="text-xs uppercase tracking-wide text-muted">
                      {t('contest.results.score')}
                    </dt>
                    <dd className="text-fg">{entry.participant.score}</dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-wide text-muted">
                      {t('contest.results.rank')}
                    </dt>
                    <dd className="text-fg">{entry.result?.rank ?? t('entries.noResult')}</dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-wide text-muted">
                      {t('contest.results.prize')}
                    </dt>
                    <dd className="text-fg">
                      {entry.result ? `${entry.result.prize} ${entry.asset}` : t('common.none')}
                    </dd>
                  </div>
                  <div>
                    <dt className="text-xs uppercase tracking-wide text-muted">
                      {t('common.date')}
                    </dt>
                    <dd className="text-fg">{formatDateTime(entry.startsAt, locale)}</dd>
                  </div>
                </dl>

                <div className="flex flex-wrap gap-2">
                  <Link to={`/contests/${entry.contestId}`}>
                    <Button variant="outline" size="sm">
                      {t('entries.viewContest')}
                    </Button>
                  </Link>
                  {entry.result && (
                    <Link to={`/contests/${entry.contestId}/results`}>
                      <Button variant="ghost" size="sm">
                        {t('contest.detail.viewResults')}
                      </Button>
                    </Link>
                  )}
                </div>
              </CardContent>
            </Card>
          </li>
        ))}
      </ul>

      <Card className="mt-10">
        <CardHeader>
          <CardTitle>{t('entries.myPayouts')}</CardTitle>
        </CardHeader>
        <CardContent>
          {payouts.length === 0 ? (
            <p className="text-sm text-muted">{t('entries.payoutsEmpty')}</p>
          ) : (
            <ul className="flex flex-col gap-3">
              {payouts.map((payout) => (
                <li
                  key={payout.id}
                  className="flex flex-wrap items-center justify-between gap-3 border-b border-border pb-3 text-sm last:border-0 last:pb-0"
                >
                  <span className="text-fg">
                    {t(payout.kind === 'prize' ? 'payout.kind.prize' : 'payout.kind.refund')}
                  </span>
                  <span className="font-medium text-fg">
                    {payout.amount} {payout.asset}
                  </span>
                  <Badge tone={payout.status === 'completed' ? 'success' : 'warning'}>
                    {t(PAYOUT_KEY[payout.status])}
                  </Badge>
                  <span className="text-xs text-muted">{formatDateTime(payout.createdAt, locale)}</span>
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
